import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Divider,
  Paper,
  IconButton,
  Stack,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SearchIcon from '@mui/icons-material/Search';
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';

const STATUS_OPTIONS = ['open', 'in_progress', 'closed'];
const ROLE_OPTIONS = ['all', 'customer', 'provider'];

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value === 'number') return value;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDate = (value) => {
  const ms = toMillis(value);
  if (!ms) return '-';
  return new Date(ms).toLocaleString();
};

const buildCaseId = (docId) => `CASE-${String(docId || '').slice(-8).toUpperCase()}`;

const statusLabel = (status) => {
  switch (String(status || 'open').toLowerCase()) {
    case 'in_progress':
      return 'In Progress';
    case 'closed':
      return 'Closed';
    default:
      return 'Open';
  }
};

const statusColor = (status) => {
  switch (String(status || 'open').toLowerCase()) {
    case 'in_progress':
      return 'warning';
    case 'closed':
      return 'default';
    default:
      return 'success';
  }
};

const roleLabel = (role) => {
  const normalized = String(role || '').toLowerCase();
  if (normalized === 'provider') return 'Provider';
  if (normalized === 'customer') return 'Customer';
  return 'User';
};

/**
 * @param {object} [props.source] another country's support data (see utils/indiaOperationsSource.js);
 *   without it the page reads the Australian database as before.
 */
const SupportDesk = ({ source = null }) => {
  const { currentUser } = useAuth();
  const [supportCases, setSupportCases] = useState([]);
  const [selectedCaseDocId, setSelectedCaseDocId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  useEffect(() => {
    if (source) {
      return source.subscribeSupportCases(setSupportCases, (error) =>
        console.error('Failed to load support cases:', error),
      );
    }

    const casesQuery = query(
      collection(db, 'supportCases'),
      orderBy('createdAt', 'desc'),
    );

    const unsubscribe = onSnapshot(casesQuery, (snapshot) => {
      const list = snapshot.docs.map((caseDoc) => ({
        id: caseDoc.id,
        ...caseDoc.data(),
      }));

      setSupportCases(list);
    });

    return unsubscribe;
  }, [source]);

  const visibleCases = useMemo(() => {
    const searchText = search.trim().toLowerCase();

    return supportCases.filter((supportCase) => {
      const normalizedStatus = String(supportCase.status || 'open').toLowerCase();
      const normalizedRole = String(supportCase.requesterRole || '').toLowerCase();
      const caseId = supportCase.caseId || buildCaseId(supportCase.id);

      const matchesStatus = statusFilter === 'all' || normalizedStatus === statusFilter;
      const matchesRole = roleFilter === 'all' || normalizedRole === roleFilter;

      const searchable = [
        caseId,
        supportCase.subject || '',
        supportCase.requesterName || '',
        supportCase.requesterEmail || '',
      ]
        .join(' ')
        .toLowerCase();

      const matchesSearch = !searchText || searchable.includes(searchText);

      return matchesStatus && matchesRole && matchesSearch;
    });
  }, [supportCases, statusFilter, roleFilter, search]);

  useEffect(() => {
    if (!visibleCases.length) {
      setSelectedCaseDocId(null);
      return;
    }

    const hasSelected = visibleCases.some((supportCase) => supportCase.id === selectedCaseDocId);
    if (!selectedCaseDocId || !hasSelected) {
      setSelectedCaseDocId(visibleCases[0].id);
    }
  }, [visibleCases, selectedCaseDocId]);

  const selectedCase = useMemo(
    () => supportCases.find((supportCase) => supportCase.id === selectedCaseDocId) || null,
    [supportCases, selectedCaseDocId],
  );

  useEffect(() => {
    if (!selectedCaseDocId) {
      setMessages([]);
      return;
    }

    if (source) {
      return source.subscribeSupportMessages(selectedCaseDocId, setMessages, (error) =>
        console.error('Failed to load support messages:', error),
      );
    }

    const messagesQuery = query(
      collection(db, 'supportCases', selectedCaseDocId, 'messages'),
      orderBy('createdAt', 'asc'),
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const list = snapshot.docs.map((messageDoc) => ({
        id: messageDoc.id,
        ...messageDoc.data(),
      }));

      setMessages(list);
    });

    return unsubscribe;
  }, [selectedCaseDocId, source]);

  const handleStatusChange = async (newStatus) => {
    if (!selectedCase || isUpdatingStatus) return;

    setIsUpdatingStatus(true);
    try {
      if (source) {
        await source.setSupportCaseStatus(selectedCase.id, newStatus);
        return;
      }
      await updateDoc(doc(db, 'supportCases', selectedCase.id), {
        status: newStatus,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Failed to update support case status:', error);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleSendReply = async () => {
    if (!selectedCase || !replyText.trim() || isSending) return;

    const replyMessage = replyText.trim();
    const senderName =
      currentUser?.displayName ||
      currentUser?.email ||
      'Admin';

    setIsSending(true);
    try {
      if (source) {
        await source.replySupportCase(selectedCase.id, replyMessage);
        setReplyText('');
        return;
      }
      await addDoc(collection(db, 'supportCases', selectedCase.id, 'messages'), {
        senderType: 'admin',
        senderUid: currentUser?.uid || 'admin',
        senderName,
        message: replyMessage,
        createdAt: serverTimestamp(),
        via: 'dashboard',
      });

      await updateDoc(doc(db, 'supportCases', selectedCase.id), {
        status: selectedCase.status === 'closed' ? 'in_progress' : selectedCase.status || 'in_progress',
        lastMessageAt: serverTimestamp(),
        lastMessageSender: 'admin',
        lastMessagePreview: replyMessage.slice(0, 180),
        updatedAt: serverTimestamp(),
      });

      setReplyText('');
    } catch (error) {
      console.error('Failed to send support reply:', error);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 3 }}>
      <Paper sx={{ width: 420, p: 2 }}>
        <Typography variant="h6">Support Cases</Typography>
        <Typography variant="caption" color="text.secondary">
          View and respond to customer and provider support requests.
        </Typography>

        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search case ID, subject, requester"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ mb: 1 }}
          />

          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Select
              fullWidth
              size="small"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <MenuItem value="all">All Status</MenuItem>
              {STATUS_OPTIONS.map((status) => (
                <MenuItem key={status} value={status}>
                  {statusLabel(status)}
                </MenuItem>
              ))}
            </Select>

            <Select
              fullWidth
              size="small"
              value={roleFilter}
              onChange={(event) => setRoleFilter(event.target.value)}
            >
              <MenuItem value="all">All Roles</MenuItem>
              {ROLE_OPTIONS.filter((role) => role !== 'all').map((role) => (
                <MenuItem key={role} value={role}>
                  {roleLabel(role)}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2">Cases ({visibleCases.length})</Typography>
        <List sx={{ maxHeight: '65vh', overflow: 'auto' }}>
          {visibleCases.length === 0 ? (
            <ListItem>
              <ListItemText
                primary="No support cases"
                secondary="Try adjusting your filters or search terms."
              />
            </ListItem>
          ) : (
            visibleCases.map((supportCase) => {
              const caseId = supportCase.caseId || buildCaseId(supportCase.id);
              const latestTimestamp = supportCase.lastMessageAt || supportCase.createdAt;

              return (
                <ListItem key={supportCase.id} disablePadding>
                  <ListItemButton
                    selected={selectedCaseDocId === supportCase.id}
                    onClick={() => setSelectedCaseDocId(supportCase.id)}
                  >
                    <ListItemText
                      primary={`${caseId} - ${supportCase.subject || 'No subject'}`}
                      secondary={`${supportCase.requesterName || supportCase.requesterEmail || 'Unknown requester'}\n${supportCase.lastMessagePreview || ''}`}
                      secondaryTypographyProps={{
                        sx: {
                          whiteSpace: 'pre-line',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        },
                      }}
                    />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-end' }}>
                      <Chip
                        label={statusLabel(supportCase.status)}
                        size="small"
                        color={statusColor(supportCase.status)}
                      />
                      <Chip
                        label={roleLabel(supportCase.requesterRole)}
                        size="small"
                        variant="outlined"
                      />
                      <Typography variant="caption">{formatDate(latestTimestamp)}</Typography>
                    </Box>
                  </ListItemButton>
                </ListItem>
              );
            })
          )}
        </List>
      </Paper>

      <Box sx={{ flex: 1 }}>
        {!selectedCase ? (
          <Paper sx={{ p: 4 }}>
            <Typography variant="h6">No support case selected</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Select a support case from the list to view conversation details and reply.
            </Typography>
          </Paper>
        ) : (
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6">
                  {(selectedCase.caseId || buildCaseId(selectedCase.id))} - {selectedCase.subject || 'No subject'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Requester: {selectedCase.requesterName || 'Unknown'} ({selectedCase.requesterEmail || 'No email'})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Created: {formatDate(selectedCase.createdAt)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Select
                  value={String(selectedCase.status || 'open').toLowerCase()}
                  onChange={(event) => handleStatusChange(event.target.value)}
                  size="small"
                  disabled={isUpdatingStatus}
                >
                  {STATUS_OPTIONS.map((status) => (
                    <MenuItem key={status} value={status}>
                      {statusLabel(status)}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1">Conversation</Typography>
            <Box sx={{ maxHeight: '50vh', overflow: 'auto', my: 1, p: 1 }}>
              {messages.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No messages yet.
                </Typography>
              ) : (
                messages.map((message) => {
                  const isAdminMessage = String(message.senderType || '').toLowerCase() === 'admin';

                  return (
                    <Box
                      key={message.id}
                      sx={{
                        mb: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: isAdminMessage ? 'flex-end' : 'flex-start',
                      }}
                    >
                      <Paper
                        sx={{
                          p: 1.25,
                          backgroundColor: isAdminMessage ? '#E8F0FF' : '#F5F5F5',
                          maxWidth: '80%',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          {message.senderName || roleLabel(message.senderType)}
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {message.message || ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(message.createdAt)}
                        </Typography>
                      </Paper>
                    </Box>
                  );
                })
              )}
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                value={replyText}
                onChange={(event) => setReplyText(event.target.value)}
                placeholder="Reply to requester..."
                fullWidth
                size="small"
                multiline
                maxRows={4}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    handleSendReply();
                  }
                }}
              />
              <IconButton color="primary" onClick={handleSendReply} disabled={isSending || !replyText.trim()}>
                <SendIcon />
              </IconButton>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default SupportDesk;
